import { useState } from 'react';
import { Icon } from '../Icon/Icon';
import rightArrowIcon from '../../assets/images/arrow-right.svg';
import leftArrowIcon from '../../assets/images/arrow-left.svg';
import IconButton from '../IconButton/IconButton';
import { CloseButton } from '../CloseButton/CloseButton';
import StyledImage from '../StyledImage/StyledImage';
import { isVideoFile } from '../../shared/fileUtils';
import { Pencil } from 'lucide-react';

type Props = {
    images: string[],
    canRemoveImages?: boolean,
    canChangeImageOrder?: boolean,
    canEditImages?: boolean,
    onImageRemove?: (removedImageUrl: string) => void,
    onImageOrderChange?: (newOrder: string[]) => void,
    onImageEdit?: (imageUrl: string) => void
};

const ImageGallery = ({ 
  images: initialImages, 
  canRemoveImages, 
  canChangeImageOrder,
  canEditImages,
  onImageRemove, 
  onImageOrderChange,
  onImageEdit
}: Props) => {
  const [images, setImages] = useState(initialImages);
  const [visibleIndex, setVisibleIndex] = useState(0);
  const imagesToShow = 3;
  const [selectedImage, setSelectedImage] = useState(initialImages[0] || '');

  const nextImages = () => {
    if (visibleIndex < images.length - imagesToShow) {
      setVisibleIndex(visibleIndex + 1);
    }
  };

  const prevImages = () => {
    if (visibleIndex > 0) {
      setVisibleIndex(visibleIndex - 1);
    }
  };

  const removeImage = (indexToRemove: number) => {
    const removedImage = images[indexToRemove];
    const newImages = images.filter((_, index) => index !== indexToRemove);
    setImages(newImages);
    if (removedImage === selectedImage && newImages.length > 0) {
      setSelectedImage(newImages[0]);
    }
    if (onImageRemove) {
      onImageRemove(removedImage);
    }
    if (visibleIndex > 0 && visibleIndex === images.length - imagesToShow) {
      setVisibleIndex(visibleIndex - 1);
    }
  };

  const moveImageLeft = (index: number) => {
    if (index > 0) {
      const newImages = [...images];
      [newImages[index], newImages[index - 1]] = [newImages[index - 1], newImages[index]];
      setImages(newImages);
      if (onImageOrderChange) {
        onImageOrderChange(newImages);
      }
    }
  };

  const moveImageRight = (index: number) => {
    if (index < images.length - 1) {
      const newImages = [...images];
      [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
      setImages(newImages);
      if (onImageOrderChange) {
        onImageOrderChange(newImages);
      }
    }
  };

  const displayedImages = images.slice(visibleIndex, visibleIndex + imagesToShow);

  return (
    <div className="flex flex-col items-center mb-5">
      <div className="flex justify-between w-full items-center space-x-2">
        <IconButton onClick={prevImages}>
          <Icon url={leftArrowIcon} cssStyles="w-5" />
        </IconButton>
        <div className="flex space-x-1">
          {displayedImages.map((item, index) => {
            const video = isVideoFile(item);
            return (
              <div key={index} className="relative">
                {video ? (
                  <div
                    onClick={() => window.open(item, '_blank')} 
                    className="w-24 h-24 flex items-center justify-center bg-black cursor-pointer"
                  >
                    <span className="text-white">Video</span>
                  </div>
                ) : (
                  <img
                    src={item}
                    className={`w-24 h-24 object-cover cursor-pointer ${
                      item === selectedImage ? 'border-2 border-blue-500' : ''
                    }`}
                    alt=""
                    onClick={() => setSelectedImage(item)}
                  />
                )}
                { canChangeImageOrder && (
                  <div className="absolute mt-1.5 items-center top-0 left-0 flex space-x-2">
                    <IconButton onClick={() => moveImageLeft(visibleIndex + index)}>
                      <div className="bg-white/10 backdrop-blur-[1px] border border-white/30 rounded-full p-0.5 flex items-center justify-center">
                        <Icon url={leftArrowIcon} cssStyles="w-4 m-0 filter brightness-0 invert" />
                      </div>
                    </IconButton>
                    <span className="text-xs backdrop-brightness-95 bg-slate-50 px-1 rounded-lg">
                      {images.indexOf(item) + 1}
                    </span>
                    <IconButton onClick={() => moveImageRight(visibleIndex + index)}>
                      <div className="bg-white/10 backdrop-blur-[1px] border border-white/30 rounded-full p-0.5 flex items-center justify-center">
                        <Icon url={rightArrowIcon} cssStyles="w-4 filter brightness-0 invert" />
                      </div>
                    </IconButton>
                  </div>
                )}
                <div className="absolute top-0 right-0 flex flex-col gap-1">
                  {canEditImages && !video && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onImageEdit) {
                          onImageEdit(item);
                        }
                      }}
                      className="bg-blue-500 hover:bg-blue-600 text-white rounded-full p-1.5 shadow-md transition-colors"
                      title="Edit image"
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                  {canRemoveImages && (
                    <CloseButton fn={() => removeImage(visibleIndex + index)} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <IconButton onClick={nextImages}>
          <Icon url={rightArrowIcon} cssStyles="w-5" />
        </IconButton>
      </div>
      <div className="mt-4">
        {!isVideoFile(selectedImage) && (
          <StyledImage src={selectedImage} alt={selectedImage} />
        )}
      </div>
    </div>
  );
};

export default ImageGallery;
